import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaCalendarAlt, FaUsers, FaCheck, FaArrowRight } from 'react-icons/fa';
import './PanchKedar.css';

const PanchKedar = () => {
  const panchKedarTours = [
    {
      id: 'kedarnath',
      name: 'Kedarnath',
      duration: '04 Days',
      groupSize: '10-15 Group Size',
      price: '₹ 8,000/-',
      image: 'https://res.cloudinary.com/duym2bgp2/image/upload/v1754934085/hhmdragstk2azyzkfjlv.jpg',
      highlights: [
        'Sacred Kedarnath Temple',
        'Visit Bhairav Temple',
        'Spiritual experience',
        'High altitude pilgrimage'
      ],
      path: '/kedarnath-tour'
    },
    {
      id: 'madhmaheshwar',
      name: 'Madhmaheshwar',
      duration: '02 Nights 03 Days',
      groupSize: '10-15 Group Size',
      price: '₹ 6,500/-',
      image: 'https://res.cloudinary.com/duym2bgp2/image/upload/v1754934085/rchcnfir52qakqcqc1em.jpg',
      highlights: [
        'Sacred Madmaheshwar Temple',
        'Visit Budha Madmaheshwar',
        'Evening prayers experience',
        'Scenic Himalayan trek'
      ],
      path: '/madhmaheshwar-tour'
    },
    {
      id: 'tungnath',
      name: 'Tungnath',
      duration: '02 Nights 03 Days',
      groupSize: '10-15 Group Size',
      price: '₹ 4,499/-',
      image: 'https://res.cloudinary.com/duym2bgp2/image/upload/v1754934084/hua4lwsxtzobrjkxqorr.webp',
      highlights: [
        'World\'s Highest Shiva Temple',
        'Chandrashila Peak views',
        'Mini Switzerland of India',
        'Beginner-friendly trek'
      ],
      path: '/tungnath-tour'
    },
    {
      id: 'rudranath-kalpeshwar',
      name: 'Rudranath + Kalpeshwar',
      duration: '04 Nights 05 Days',
      groupSize: '10-15 Group Size',
      price: '₹ 8,500/-',
      image: 'https://res.cloudinary.com/duym2bgp2/image/upload/v1754934085/dukeakdjx7p3slfkl2km.webp',
      highlights: [
        'Rudranath Ji - Lord Shiva\'s face',
        'Kalpeshwar - year-round accessible',
        'Serene forests & alpine meadows',
        'Evening aarti ceremonies'
      ],
      path: '/rudranath-tour'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Panch Kedar - NomadTrailz</title>
        <meta name="description" content="Explore the sacred Panch Kedar pilgrimage sites - Kedarnath, Madhmaheshwar, Tungnath, Rudranath, and Kalpeshwar." />
      </Helmet>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
        {/* Hero Section */}
        <section className="panch-kedar-hero">
          <div className="hero-background">
            <img src="https://res.cloudinary.com/duym2bgp2/image/upload/v1755000498/panch-kedar-hero.jpg" alt="Panch Kedar" />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.8 }}
            >
              Panch Kedar
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Choose your preferred Panch Kedar pilgrimage
            </motion.p>
          </div>
        </section>

        {/* Tours Section */}
        <section className="panch-kedar-tours">
          <div className="container">
            <div className="tours-grid">
              {panchKedarTours.map((tour, index) => (
                <motion.div
                  key={tour.id}
                  className="tour-card"
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -10 }}
                >
                  <div className="tour-image">
                    <img src={tour.image} alt={tour.name} />
                  </div>
                  <div className="tour-content">
                    <h3>{tour.name}</h3>
                    <div className="tour-details">
                      <div className="detail-item">
                        <FaCalendarAlt className="detail-icon" />
                        <span>{tour.duration}</span>
                      </div>
                      <div className="detail-item">
                        <FaUsers className="detail-icon" />
                        <span>{tour.groupSize}</span>
                      </div>
                    </div>
                    <div className="tour-price">
                      <span className="price-label">Starting From</span>
                      <span className="price-amount">{tour.price}</span>
                    </div>
                    <div className="tour-highlights">
                      {tour.highlights.map((highlight, idx) => (
                        <div key={idx} className="highlight-item">
                          {highlight}
                        </div>
                      ))}
                    </div>
                    <Link to={tour.path} className="view-details-btn">
                      View Details
                      <FaArrowRight className="btn-icon" />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* About Section */}
        <section className="panch-kedar-about">
          <div className="container">
            <motion.div 
              className="about-content"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2>About Panch Kedar</h2>
              <p>
                Panch Kedar refers to five sacred temples of Lord Shiva in the Garhwal Himalayas. 
                These temples are Kedarnath, Madhmaheshwar, Tungnath, Rudranath, and Kalpeshwar. 
                According to Hindu mythology, after the Mahabharata war, the Pandavas sought forgiveness 
                from Lord Shiva for killing their own kin. Shiva, unwilling to forgive them, took the form 
                of a bull and hid in the Himalayas. The Pandavas followed him, and Shiva's body parts 
                appeared at five different locations, which became the Panch Kedar temples.
              </p>
              <p>
                Each temple represents a different part of Lord Shiva's body:
              </p>
              <ul>
                <li><strong>Kedarnath:</strong> The hump of the bull (Lord Shiva's back)</li>
                <li><strong>Madhmaheshwar:</strong> The navel of the bull (Lord Shiva's stomach)</li>
                <li><strong>Tungnath:</strong> The arms of the bull (Lord Shiva's arms)</li>
                <li><strong>Rudranath:</strong> The face of the bull (Lord Shiva's face)</li>
                <li><strong>Kalpeshwar:</strong> The hair of the bull (Lord Shiva's hair)</li>
              </ul>
            </motion.div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default PanchKedar;